/**
 * Delivery Time Mapping
 *
 * Zentrale Datei für Lieferzeit-Mappings aus dem Meilisearch products Index.
 * Die delivery_time Werte sind Arrays mit Slugs wie ['sofort-lieferbar'].
 *
 * Verwendung:
 * import { getDeliveryDays, getDeliveryLabel, calculateDeliveryDate } from '@/lib/delivery-time-mapping'
 */

/**
 * Mapping von delivery_time Slug zu Tagen
 * Die Werte sind Durchschnittswerte für die Berechnung
 */
export const deliveryTimeDaysMap: Record<string, number> = {
    // Sofort verfügbar
    'sofort-lieferbar': 3,
    'lagerware': 3,
    'auf-lager': 3,

    // Kurze Lieferzeit
    '1-2-werktage': 2,
    '1-3-werktage': 3,
    '2-3-werktage': 3,
    '3-5-werktage': 5,
    'bis-1-woche': 7,

    // Mittlere Lieferzeit
    'bis-2-wochen': 14,
    '1-2-wochen': 14,
    '2-3-wochen': 21,
    'bis-3-wochen': 21,

    // Längere Lieferzeit
    'bis-4-wochen': 28,
    '3-4-wochen': 28,
    '1-monat': 30,
    'bis-5-wochen': 35,
    '4-5-wochen': 35,

    // Lange Lieferzeit
    'bis-6-wochen': 42,
    '5-6-wochen': 42,
    'bis-7-wochen': 49,
    '6-7-wochen': 49,
    'bis-8-wochen': 56,
    '7-8-wochen': 56,

    // Sehr lange Lieferzeit
    'mehr-als-8-wochen': 70,
    '8-10-wochen': 63,
    '10-12-wochen': 77,
    'mehr-als-10-wochen': 84,
    'mehr-als-12-wochen': 98,

    // Spezialanfertigung
    'auf-anfrage': 60,
    'sonderanfertigung': 60,
};

/**
 * Mapping von delivery_time Slug zu lesbarem Label
 */
export const deliveryTimeLabelMap: Record<string, string> = {
    // Sofort verfügbar
    'sofort-lieferbar': 'Sofort lieferbar',
    'lagerware': 'Sofort lieferbar',
    'auf-lager': 'Sofort lieferbar',

    // Kurze Lieferzeit
    '1-2-werktage': '1-2 Werktage',
    '1-3-werktage': '1-3 Werktage',
    '2-3-werktage': '2-3 Werktage',
    '3-5-werktage': '3-5 Werktage',
    'bis-1-woche': 'ca. 1 Woche',

    // Mittlere Lieferzeit
    'bis-2-wochen': 'ca. 2 Wochen',
    '1-2-wochen': '1-2 Wochen',
    '2-3-wochen': '2-3 Wochen',
    'bis-3-wochen': 'ca. 3 Wochen',

    // Längere Lieferzeit
    'bis-4-wochen': 'ca. 4 Wochen',
    '3-4-wochen': '3-4 Wochen',
    '1-monat': 'ca. 1 Monat',
    'bis-5-wochen': 'ca. 5 Wochen',
    '4-5-wochen': '4-5 Wochen',

    // Lange Lieferzeit
    'bis-6-wochen': 'ca. 6 Wochen',
    '5-6-wochen': '5-6 Wochen',
    'bis-7-wochen': 'ca. 7 Wochen',
    '6-7-wochen': '6-7 Wochen',
    'bis-8-wochen': 'ca. 8 Wochen',
    '7-8-wochen': '7-8 Wochen',

    // Sehr lange Lieferzeit
    'mehr-als-8-wochen': '8+ Wochen',
    '8-10-wochen': '8-10 Wochen',
    '10-12-wochen': '10-12 Wochen',
    'mehr-als-10-wochen': '10+ Wochen',
    'mehr-als-12-wochen': '12+ Wochen',

    // Spezialanfertigung
    'auf-anfrage': 'Auf Anfrage',
    'sonderanfertigung': 'Sonderanfertigung',
};

/**
 * Standard-Werte wenn kein Mapping gefunden wird
 */
export const DEFAULT_DELIVERY_DAYS = 14;
export const DEFAULT_DELIVERY_LABEL = 'ca. 2-4 Wochen';

/**
 * Holt die Anzahl der Liefertage für einen delivery_time Slug
 *
 * @param deliveryTimeSlug - Der Slug aus dem delivery_time Array
 * @returns Anzahl der Tage bis zur Lieferung
 */
export function getDeliveryDays(deliveryTimeSlug: string): number {
    if (!deliveryTimeSlug) return DEFAULT_DELIVERY_DAYS;
    return deliveryTimeDaysMap[deliveryTimeSlug] || DEFAULT_DELIVERY_DAYS;
}

/**
 * Holt das lesbare Label für einen delivery_time Slug
 *
 * @param deliveryTimeSlug - Der Slug aus dem delivery_time Array
 * @returns Lesbares Label für die Lieferzeit
 */
export function getDeliveryLabel(deliveryTimeSlug: string): string {
    if (!deliveryTimeSlug) return DEFAULT_DELIVERY_LABEL;
    return deliveryTimeLabelMap[deliveryTimeSlug] || DEFAULT_DELIVERY_LABEL;
}

/**
 * Berechnet das voraussichtliche Lieferdatum basierend auf dem delivery_time Slug
 *
 * WICHTIG für Next.js 16 Cache Components:
 * Diese Funktion sollte nur aufgerufen werden, nachdem bereits Daten gefetcht wurden,
 * oder mit einem explizit übergebenen Datum.
 *
 * @param deliveryTime - Array mit delivery_time Slugs (z.B. ['bis-2-wochen'])
 * @param referenceDate - Optionales Referenzdatum (für Server Components empfohlen)
 * @returns Formatiertes Datum als String (z.B. "15. Januar")
 */
export function calculateDeliveryDate(deliveryTime: string[], referenceDate?: Date): string {
    const timeString = deliveryTime?.[0] || '';
    const daysToAdd = getDeliveryDays(timeString);

    // Use provided date or create new one (caller must ensure this runs after data fetch)
    const now = referenceDate || new Date();
    const deliveryDate = new Date(now.getTime() + daysToAdd * 24 * 60 * 60 * 1000);

    return deliveryDate.toLocaleDateString('de-DE', {day: 'numeric', month: 'long'});
}

/**
 * Gibt nur die Anzahl der Tage bis zur Lieferung zurück.
 * Sicher für Server Components, da kein Date() verwendet wird.
 *
 * @param deliveryTime - Array mit delivery_time Slugs
 * @returns Anzahl der Tage
 */
export function getDeliveryDaysFromArray(deliveryTime: string[]): number {
    const timeString = deliveryTime?.[0] || '';
    return getDeliveryDays(timeString);
}

/**
 * Returns a static delivery description for use in Server Components.
 * This is safe for Next.js 16 Cache Components as it doesn't use Date().
 *
 * @param deliveryTimeSlug - The delivery time slug
 * @returns A relative time description
 */
export function getStaticDeliveryDescription(deliveryTimeSlug: string): string {
    const daysToAdd = getDeliveryDays(deliveryTimeSlug);
    if (daysToAdd <= 7) return "innerhalb einer Woche";
    if (daysToAdd <= 14) return "in ca. 2 Wochen";
    if (daysToAdd <= 28) return "in ca. 4 Wochen";
    if (daysToAdd <= 42) return "in ca. 6 Wochen";
    if (daysToAdd <= 56) return "in ca. 8 Wochen";
    return "in ca. 8+ Wochen";
}

/**
 * Holt den lesbaren Text für ein delivery_time Array
 *
 * @param deliveryTime - Array mit delivery_time Slugs
 * @returns Lesbares Label
 */
export function getDeliveryTimeText(deliveryTime: string[]): string {
    const timeString = deliveryTime?.[0] || '';
    return getDeliveryLabel(timeString);
}

/**
 * Prüft ob ein delivery_time Slug bekannt ist
 */
export function hasDeliveryTimeMapping(deliveryTimeSlug: string): boolean {
    return deliveryTimeSlug in deliveryTimeDaysMap;
}

/**
 * Gibt alle verfügbaren delivery_time Slugs zurück
 */
export function getAvailableDeliveryTimeSlugs(): string[] {
    return Object.keys(deliveryTimeDaysMap);
}

/**
 * Lieferzeit-Kategorien für UI-Gruppierung
 */
export const deliveryTimeCategories = {
    immediate: ['sofort-lieferbar', 'lagerware', 'auf-lager', '1-2-werktage', '1-3-werktage'],
    short: ['2-3-werktage', '3-5-werktage', 'bis-1-woche', 'bis-2-wochen', '1-2-wochen'],
    medium: ['2-3-wochen', 'bis-3-wochen', 'bis-4-wochen', '3-4-wochen', '1-monat'],
    long: ['bis-5-wochen', 'bis-6-wochen', '5-6-wochen', 'bis-7-wochen', 'bis-8-wochen'],
    veryLong: ['mehr-als-8-wochen', '8-10-wochen', '10-12-wochen', 'mehr-als-10-wochen', 'mehr-als-12-wochen'],
} as const;
